// The card graphics are various HTML5 canvas drawings.

import type { Color, Rank, Suit, Variant } from "@hanabi-live/game";
import {
  START_CARD_RANK,
  getSuit,
  getSuitAbbreviationForVariant,
} from "@hanabi-live/game";
import { CARD_H, CARD_W } from "./constants";
import { drawPip } from "./drawPip";
import { drawStylizedRank } from "./drawStylizedRank";

enum CardArea {
  Number,
  Background,
}

/** Represents a stack base. Only used in this file for image drawing purposes. */
const STACK_BASE_RANK = 0;

/**
 * Represents an unknown card (e.g. the gray card with pips on it). Only used in this file for image
 * drawing purposes.
 */
const UNKNOWN_CARD_RANK = 6;

export const CARD_IMAGE_STACK_BASE_RANK_NAME = "StackBase";
export const CARD_IMAGE_UNKNOWN_CARD_RANK_NAME = "UnknownRank";

/** Returns an object containing all of the drawn cards images (on individual canvases). */
export function drawCards(
  variant: Variant,
  colorblindMode: boolean,
  styleNumbers: boolean,
  enableShadows: boolean,
  initCanvas: () => { cvs: HTMLCanvasElement; ctx: CanvasRenderingContext2D },
  cloneCanvas: (
    oldCvs: HTMLCanvasElement,
    oldCtx: CanvasRenderingContext2D,
  ) => HTMLCanvasElement,
  saveCanvas: (
    cvs: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
  ) => HTMLCanvasElement,
): ReadonlyMap<string, HTMLCanvasElement> {
  const cardImages = new Map<string, HTMLCanvasElement>();

  // Add the "Unknown" suit to the list of suits for this variant. The unknown suit has blank white
  // cards, representing cards of known rank but unknown suit.
  const unknownSuit = getSuit("Unknown");
  const suits = [...variant.suits, unknownSuit];

  let suitIndex = -1;
  for (const suit of suits) {
    suitIndex++;

    for (const rank of [
      STACK_BASE_RANK,
      1,
      2,
      3,
      4,
      5,
      UNKNOWN_CARD_RANK,
      START_CARD_RANK,
    ] as const) {
      // We need unknown cards for 1, 2, 3, 4, 5, and the "START" card.
      if (
        suit.name === "Unknown"
        && (rank === STACK_BASE_RANK || rank === UNKNOWN_CARD_RANK)
      ) {
        continue;
      }

      const { cvs, ctx } = initCanvas();

      // We do not need the background on the stack base.
      if (rank !== STACK_BASE_RANK) {
        drawCardBackground(ctx, enableShadows);
      }

      // Make the special corners on the cards for dual-color suits.
      drawDualColorTriangles(ctx, suit.clueColors, enableShadows);

      // Draw the background and the borders around the card.
      drawCardBase(ctx, suit, rank, variant, colorblindMode, enableShadows);

      ctx.shadowBlur = 10;
      ctx.fillStyle = getSuitStyle(
        suit,
        rank,
        ctx,
        CardArea.Number,
        variant,
        colorblindMode,
      );
      ctx.strokeStyle = "black";
      ctx.lineWidth = enableShadows ? 2 : 8;
      ctx.lineJoin = "round";

      if (rank !== STACK_BASE_RANK && rank !== UNKNOWN_CARD_RANK) {
        let textYPos: number;
        let rankLabel = rank.toString();
        if (rank === START_CARD_RANK) {
          rankLabel = "S";
        }
        let fontSize: number;
        if (colorblindMode) {
          rankLabel += getSuitAbbreviationForVariant(suit, variant);
          fontSize = 68;
          textYPos = 83;
        } else {
          fontSize = 96;
          textYPos = 110;
        }

        ctx.font = `bold ${fontSize}pt Arial`;

        // Draw the rank on the top left.
        ctx.save();
        if (styleNumbers && !colorblindMode) {
          drawStylizedRank(ctx, rank);
          ctx.fill();
          ctx.stroke();
        } else {
          drawText(ctx, textYPos, rankLabel, enableShadows);
        }
        if (
          variant.specialRankDeceptive
          && rank === variant.specialRank
          && suit.name !== "Unknown"
          && !suit.noClueRanks
        ) {
          const deceptiveRank =
            variant.clueRanks[suitIndex % variant.clueRanks.length]!;
          if (colorblindMode) {
            ctx.translate(CARD_W / 20, CARD_H / 5);
            ctx.scale(0.65, 0.65);
          } else {
            ctx.translate(CARD_W / 4 + CARD_W / 50, CARD_H / 40);
            ctx.scale(0.5, 0.5);
          }
          if (styleNumbers && !colorblindMode) {
            drawStylizedRank(ctx, deceptiveRank);
            ctx.fill();
            ctx.stroke();
          } else {
            drawText(ctx, textYPos, `${deceptiveRank}`, enableShadows);
          }
        }
        ctx.restore();

        // "Index" cards are used to draw cards of learned but not yet known rank
        // (e.g. for in-game replays)
        const cardImagesIndex = `Index-${suit.name}-${rank}`;
        cardImages.set(cardImagesIndex, cloneCanvas(cvs, ctx));

        // Draw the rank on the bottom right.
        if (!variant.upOrDown && !suit.reversed) {
          ctx.save();
          ctx.translate(CARD_W, CARD_H);
          ctx.rotate(Math.PI);
          if (styleNumbers && !colorblindMode) {
            drawStylizedRank(ctx, rank);
            ctx.fill();
            ctx.stroke();
          } else {
            drawText(ctx, textYPos, rankLabel, enableShadows);
          }
          ctx.restore();
        }
      }

      // The "Unknown" suit does not have pips. (It is a white suit that is used for cards that are
      // clued with rank.)
      if (suit.name !== "Unknown") {
        drawSuitPips(ctx, rank, suit, variant, colorblindMode, enableShadows);
      }

      const cardImageRankName = getCardImageRankName(rank);
      const cardImagesIndex = `card-${suit.name}-${cardImageRankName}`;
      const cardImage = saveCanvas(cvs, ctx);
      cardImages.set(cardImagesIndex, cardImage);
    }
  }

  // Unknown 6 is a card that is completely unknown. This is a special case; we want to render
  // completely unknown cards as a blank gray card (instead of a blank white card).
  {
    const { cvs, ctx } = makeUnknownCard(initCanvas, enableShadows);
    const cardUnknown = saveCanvas(cvs, ctx);
    cardImages.set(
      `card-Unknown-${CARD_IMAGE_UNKNOWN_CARD_RANK_NAME}`,
      cardUnknown,
    );
  }

  // Additionally, create an image for the deck back. This is similar to the Unknown 6 card, except
  // it has pips for each suit.
  {
    const { cvs, ctx } = makeDeckBack(variant, initCanvas, enableShadows);
    const deckBack = saveCanvas(cvs, ctx);
    cardImages.set("deck-back", deckBack);
  }

  return cardImages;
}

function getCardImageRankName(
  rank: Rank | typeof STACK_BASE_RANK | typeof UNKNOWN_CARD_RANK,
): string {
  switch (rank) {
    case STACK_BASE_RANK: {
      return CARD_IMAGE_STACK_BASE_RANK_NAME;
    }

    case UNKNOWN_CARD_RANK: {
      return CARD_IMAGE_UNKNOWN_CARD_RANK_NAME;
    }

    default: {
      return rank.toString();
    }
  }
}

function drawSuitPips(
  ctx: CanvasRenderingContext2D,
  rank: Rank | typeof STACK_BASE_RANK | typeof UNKNOWN_CARD_RANK,
  suit: Suit,
  variant: Variant,
  colorblindMode: boolean,
  enableShadows: boolean,
) {
  const scale = 0.4;

  // The middle for card 1.
  if (rank === 1) {
    ctx.save();
    ctx.translate(CARD_W / 2, CARD_H / 2);
    ctx.scale(scale * 1.8, scale * 1.8);
    drawPip(ctx, suit, variant, enableShadows);
    ctx.restore();
  }

  // Top and bottom for card 2.
  if (rank === 2) {
    const symbolYPos = colorblindMode ? 60 : 90;
    ctx.save();
    ctx.translate(CARD_W / 2, CARD_H / 2);
    ctx.translate(0, -symbolYPos);
    ctx.scale(scale * 1.4, scale * 1.4);
    drawPip(ctx, suit, variant, enableShadows);
    ctx.restore();

    ctx.save();
    ctx.translate(CARD_W / 2, CARD_H / 2);
    ctx.translate(0, symbolYPos);
    ctx.scale(scale * 1.4, scale * 1.4);
    ctx.rotate(Math.PI);
    drawPip(ctx, suit, variant, enableShadows);
    ctx.restore();
  }

  // Top and bottom for cards 3, 4, 5.
  if (rank >= 3 && rank <= 5) {
    const symbolYPos = colorblindMode ? 80 : 120;
    ctx.save();
    ctx.translate(CARD_W / 2, CARD_H / 2);
    ctx.translate(0, -symbolYPos);
    ctx.scale(scale, scale);
    drawPip(ctx, suit, variant, enableShadows);
    ctx.restore();

    ctx.save();
    ctx.translate(CARD_W / 2, CARD_H / 2);
    ctx.translate(0, symbolYPos);
    ctx.scale(scale, scale);
    ctx.rotate(Math.PI);
    drawPip(ctx, suit, variant, enableShadows);
    ctx.restore();
  }

  // Left and right for cards 4 and 5.
  if (rank === 4 || rank === 5) {
    ctx.save();
    ctx.translate(CARD_W / 2, CARD_H / 2);
    ctx.translate(-90, 0);
    ctx.scale(scale, scale);
    drawPip(ctx, suit, variant, enableShadows);
    ctx.restore();

    ctx.save();
    ctx.translate(CARD_W / 2, CARD_H / 2);
    ctx.translate(90, 0);
    ctx.scale(scale, scale);
    ctx.rotate(Math.PI);
    drawPip(ctx, suit, variant, enableShadows);
    ctx.restore();
  }

  // Size, position, and alpha adjustment for the central icon on 3 and 5.
  if (rank === 3 || rank === 5) {
    ctx.globalAlpha = 1;
    ctx.save();
    ctx.translate(CARD_W / 2, CARD_H / 2);
    ctx.scale(scale * 1.2, scale * 1.2);
    drawPip(ctx, suit, variant, enableShadows);
    ctx.restore();
  }

  // Size, position, and alpha adjustment for the central icon on stack base.
  if (rank === STACK_BASE_RANK) {
    ctx.globalAlpha = 1;
    ctx.save();
    ctx.translate(CARD_W / 2, CARD_H / 2);
    ctx.scale(scale * 2.5, scale * 2.5);
    drawPip(ctx, suit, variant, enableShadows);
    ctx.restore();
  }

  // Draw large suit pip.
  if (rank === UNKNOWN_CARD_RANK || rank === START_CARD_RANK) {
    ctx.save();
    let lineWidth: number | undefined;
    if (rank === START_CARD_RANK) {
      lineWidth = 2;
    } else {
      // Make the pip faint.
      ctx.globalAlpha = colorblindMode ? 0.4 : 0.1;
    }
    ctx.translate(CARD_W / 2, CARD_H / 2);
    ctx.scale(scale * 3, scale * 3);
    drawPip(ctx, suit, variant, enableShadows, undefined, lineWidth);
    ctx.restore();
  }
}

function makeUnknownCard(
  initCanvas: () => { cvs: HTMLCanvasElement; ctx: CanvasRenderingContext2D },
  enableShadows: boolean,
): {
  cvs: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
} {
  const { cvs, ctx } = initCanvas();

  drawCardBackground(ctx, enableShadows);
  ctx.fillStyle = "black";
  cardBorderPath(ctx, 4, enableShadows);

  ctx.save();
  ctx.globalAlpha = 0.5;
  ctx.fill();
  ctx.globalAlpha = 0.6;
  ctx.lineWidth = 8;
  ctx.stroke();
  ctx.restore();

  ctx.fillStyle = "#444444";
  ctx.lineWidth = 8;
  ctx.lineJoin = "round";

  ctx.translate(CARD_W / 2, CARD_H / 2);

  return {
    cvs,
    ctx,
  };
}

function makeDeckBack(
  variant: Variant,
  initCanvas: () => { cvs: HTMLCanvasElement; ctx: CanvasRenderingContext2D },
  enableShadows: boolean,
): {
  cvs: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
} {
  const { cvs, ctx } = makeUnknownCard(initCanvas, enableShadows);

  const sf = 0.4; // Scale factor
  const nSuits = variant.suits.length;
  ctx.scale(sf, sf);

  for (const [suitIndex, suit] of variant.suits.entries()) {
    // Transform polar to cartesian coordinates.
    const x =
      -1.05
      * Math.floor(
        CARD_W * 0.7 * Math.cos((-suitIndex / nSuits + 0.25) * Math.PI * 2),
      );
    const y =
      -1.05
      * Math.floor(
        CARD_W * 0.7 * Math.sin((-suitIndex / nSuits + 0.25) * Math.PI * 2),
      );

    ctx.save();
    ctx.translate(x, y);
    drawPip(ctx, suit, variant, true, "#444444"); // Pips on the back of the deck should be gray.
    ctx.restore();
  }

  ctx.scale(1 / sf, 1 / sf);

  return {
    cvs,
    ctx,
  };
}

function drawCardBase(
  ctx: CanvasRenderingContext2D,
  suit: Suit,
  rank: Rank | typeof STACK_BASE_RANK | typeof UNKNOWN_CARD_RANK,
  variant: Variant,
  colorblindMode: boolean,
  enableShadows: boolean,
) {
  // Draw the background.
  ctx.save();
  ctx.fillStyle = getSuitStyle(
    suit,
    rank,
    ctx,
    CardArea.Background,
    variant,
    colorblindMode,
  );
  ctx.strokeStyle = getSuitStyle(
    suit,
    rank,
    ctx,
    CardArea.Background,
    variant,
    colorblindMode,
  );
  cardBorderPath(ctx, 4, enableShadows);

  // Draw the borders (on visible cards) and the color fill.
  ctx.globalAlpha = 0.3;
  ctx.fill();
  ctx.globalAlpha = 0.7;
  ctx.lineWidth = 8;
  // The borders should be more opaque for the stack base.
  if (rank === 0) {
    ctx.globalAlpha = 1;
  }
  ctx.stroke();
  ctx.restore();
}

function cardBorderPath(
  ctx: CanvasRenderingContext2D,
  padding: number,
  enableShadows: boolean,
) {
  const roundedCornerSeverity = enableShadows ? 0.08 : 0.12;
  const xRadians = CARD_W * roundedCornerSeverity;
  const yRadians = CARD_W * roundedCornerSeverity;
  // (We want them to both have the same value so that the curve has a 45 degree angle.)
  ctx.beginPath();
  ctx.moveTo(padding, yRadians + padding); // Top-left corner
  ctx.lineTo(padding, CARD_H - yRadians - padding); // Bottom-left corner
  ctx.quadraticCurveTo(0, CARD_H, xRadians + padding, CARD_H - padding);
  ctx.lineTo(CARD_W - xRadians - padding, CARD_H - padding); // Bottom-right corner
  ctx.quadraticCurveTo(
    CARD_W,
    CARD_H,
    CARD_W - padding,
    CARD_H - yRadians - padding,
  );
  ctx.lineTo(CARD_W - padding, yRadians + padding); // Top-right corner
  ctx.quadraticCurveTo(CARD_W, 0, CARD_W - xRadians - padding, padding);
  ctx.lineTo(xRadians + padding, padding); // Top-left corner
  ctx.quadraticCurveTo(0, 0, padding, yRadians + padding);
}

function drawShape(ctx: CanvasRenderingContext2D, enableShadows: boolean) {
  if (enableShadows) {
    ctx.shadowColor = "rgba(0, 0, 0, 0.9)";
  }
  ctx.fill();
  if (enableShadows) {
    ctx.shadowColor = "rgba(0, 0, 0, 0)";
  }
  ctx.stroke();
}

function drawText(
  ctx: CanvasRenderingContext2D,
  textYPos: number,
  indexLabel: string,
  enableShadows: boolean,
) {
  ctx.save();
  if (enableShadows) {
    ctx.shadowColor = "rgba(0, 0, 0, 0.9)";
    ctx.fillText(indexLabel, 19, textYPos);
    ctx.shadowColor = "rgba(0, 0, 0, 0)";
    ctx.strokeText(indexLabel, 19, textYPos);
  } else {
    ctx.strokeText(indexLabel, 19, textYPos);
    ctx.fillText(indexLabel, 19, textYPos);
  }
  ctx.restore();
}

function drawDualColorTriangles(
  ctx: CanvasRenderingContext2D,
  clueColors: readonly Color[],
  enableShadows: boolean,
) {
  const count = clueColors.length;
  if (count < 2) {
    return;
  }

  ctx.save();
  ctx.lineWidth = 1;
  const triangleSize = 50;
  const borderSize = 8;
  const sliceAngle = Math.PI / 2 / count;

  // Top-right
  drawColorTriangle(
    ctx,
    clueColors,
    CARD_W - borderSize,
    borderSize,
    Math.PI,
    -1,
    triangleSize,
    sliceAngle,
    enableShadows,
  );

  // Bottom-left
  drawColorTriangle(
    ctx,
    clueColors,
    borderSize,
    CARD_H - borderSize,
    1.5 * Math.PI,
    1,
    triangleSize,
    sliceAngle,
    enableShadows,
  );

  ctx.restore();
}

function drawColorTriangle(
  ctx: CanvasRenderingContext2D,
  clueColors: readonly Color[],
  cx: number,
  cy: number,
  startTheta: number,
  direction: number,
  triangleSize: number,
  sliceAngle: number,
  enableShadows: boolean,
) {
  for (const [index, color] of clueColors.entries()) {
    const theta1 = startTheta + index * sliceAngle * direction;
    const theta2 = startTheta + (index + 1) * sliceAngle * direction;

    // Calculate the length of the ray from the corner to the hypotenuse. For a square/diamond shape
    // (Manhattan distance), `r = size / (|cos| + |sin|)` ensures the point lies on the straight
    // diagonal line.
    const r1 =
      triangleSize / (Math.abs(Math.cos(theta1)) + Math.abs(Math.sin(theta1)));
    const r2 =
      triangleSize / (Math.abs(Math.cos(theta2)) + Math.abs(Math.sin(theta2)));

    ctx.beginPath();
    ctx.moveTo(cx, cy); // Start at corner.

    // Point on hypotenuse for start of slice.
    ctx.lineTo(cx + Math.cos(theta1) * r1, cy + Math.sin(theta1) * r1);

    // Point on hypotenuse for end of slice.
    ctx.lineTo(cx + Math.cos(theta2) * r2, cy + Math.sin(theta2) * r2);

    ctx.moveTo(cx, cy); // Return to corner to close path for filling.
    ctx.fillStyle = color.fill;

    drawShape(ctx, enableShadows);
  }
}

function drawCardBackground(
  ctx: CanvasRenderingContext2D,
  enableShadows: boolean,
) {
  ctx.save();
  cardBorderPath(ctx, 4, enableShadows);

  ctx.fillStyle = "white";
  ctx.fill();

  ctx.restore();
}

function getSuitStyle(
  suit: Suit,
  rank: Rank | typeof STACK_BASE_RANK | typeof UNKNOWN_CARD_RANK,
  ctx: CanvasRenderingContext2D,
  cardArea: CardArea,
  variant: Variant,
  colorblindMode: boolean,
) {
  if (cardArea === CardArea.Number) {
    // In Synesthesia variants, color the number itself with the color that it contributes to the
    // card.
    if (variant.synesthesia) {
      if (rank === STACK_BASE_RANK) {
        return suit.fill;
      }

      // If the suit does not get clued by its rank, then coloring the rank is misleading, so use
      // the suit color.
      if (suit.noClueRanks) {
        return suit.fill;
      }
      const prismColorIndex = (rank - 1) % variant.clueColors.length;

      return variant.clueColors[prismColorIndex]!.fill;
    }

    if (rank === variant.specialRank) {
      const allClueColors =
        suit.allClueColors
        || (variant.specialRankAllClueColors && !suit.noClueColors);
      const noClueColors =
        suit.noClueColors
        || (variant.specialRankNoClueColors && !suit.allClueColors);
      const allClueRanks =
        suit.allClueRanks
        || (variant.specialRankAllClueRanks && !suit.noClueRanks);
      const noClueRanks =
        suit.noClueRanks
        || (variant.specialRankNoClueRanks && !suit.allClueRanks);
      const dark = suit.oneOfEach;

      if (allClueColors) {
        if (allClueRanks) {
          const rainbow = getSuit(dark ? "Dark Omni" : "Omni");
          return evenLinearGradient(ctx, rainbow.fillColors, [0, 14, 0, 110]);
        }

        if (noClueRanks) {
          const rainbow = getSuit(dark ? "Cocoa Rainbow" : "Muddy Rainbow");
          return evenLinearGradient(ctx, rainbow.fillColors, [0, 14, 0, 110]);
        }

        const rainbow = getSuit(dark ? "Dark Rainbow" : "Rainbow");
        return evenLinearGradient(ctx, rainbow.fillColors, [0, 14, 0, 110]);
      }

      if (noClueColors) {
        if (allClueRanks) {
          return getSuit(dark ? "Gray Pink" : "Light Pink").fill;
        }

        if (noClueRanks) {
          return getSuit(dark ? "Dark Null" : "Null").fill;
        }

        return getSuit(dark ? "Gray" : "White").fill;
      }

      if (allClueRanks) {
        return getSuit(dark ? "Dark Pink" : "Pink").fill;
      }

      if (noClueRanks) {
        return getSuit(dark ? "Dark Brown" : "Brown").fill;
      }
    }
  }

  if (suit.prism) {
    // Prism cards have a custom color depending on their rank.
    if (rank === 0) {
      return suit.fill;
    }

    let prismColorIndex = (rank - 1) % variant.clueColors.length;
    if (rank === START_CARD_RANK) {
      // "START" cards count as rank 0, so they are touched by the final color.
      prismColorIndex = variant.clueColors.length - 1;
    }

    const fillToMixHex = variant.clueColors[prismColorIndex]!.fill;
    const fillToMixRGB = hexToRGB(fillToMixHex);
    if (fillToMixRGB === undefined) {
      return suit.fill;
    }

    const fillToMixArray = [fillToMixRGB.r, fillToMixRGB.g, fillToMixRGB.b];
    let fillToMixArray2 = [255, 255, 255]; // White
    if (suit.oneOfEach) {
      fillToMixArray2 = [0, 0, 0]; // Black
    }

    return colorMixer(fillToMixArray, fillToMixArray2, 0.5); // Mix it with white by 50%
  }

  // Nearly all other suits have a solid fill.
  if (suit.fill !== "multi") {
    return colorblindMode ? suit.fillColorblind : suit.fill;
  }

  // Rainbow suits use a gradient fill, but the specific type of gradient will depend on the
  // specific element of the card that we are filling in.
  switch (cardArea) {
    case CardArea.Number: {
      return evenLinearGradient(ctx, suit.fillColors, [0, 14, 0, 110]);
    }

    case CardArea.Background: {
      if (suit.name === "Omni" || suit.name === "Dark Omni") {
        return evenLinearGradient(ctx, suit.fillColors, [
          0,
          -30,
          0,
          CARD_H + 30,
        ]);
      }

      return evenLinearGradient(ctx, suit.fillColors, [0, 0, CARD_W, CARD_H]);
    }
  }
}

/** Generates a vertical gradient that is evenly distributed between its component colors. */
function evenLinearGradient(
  ctx: CanvasRenderingContext2D,
  colors: readonly string[],
  args: readonly number[],
) {
  const grad = ctx.createLinearGradient(args[0]!, args[1]!, args[2]!, args[3]!);

  for (const [i, color] of colors.entries()) {
    grad.addColorStop(i / (colors.length - 1), color);
  }

  return grad;
}

/**
 * From: https://stackoverflow.com/questions/14819058/mixing-two-colors-naturally-in-javascript
 *
 * @param colorChannelA Integer ranging from 0 to 255.
 * @param colorChannelB Integer ranging from 0 to 255.
 * @param amountToMix Float ranging from 0.0 to 1.0.
 */
function colorChannelMixer(
  colorChannelA: number,
  colorChannelB: number,
  amountToMix: number,
) {
  const channelA = colorChannelA * amountToMix;
  const channelB = colorChannelB * (1 - amountToMix);

  return channelA + channelB;
}

/**
 * From: https://stackoverflow.com/questions/14819058/mixing-two-colors-naturally-in-javascript
 *
 * - `rgbA` and `rgbB` are arrays.
 * - `amountToMix` ranges from 0.0 to 1.0.
 * - For example: `(red): rgbA = [255,0,0]`
 */
function colorMixer(
  rgbA: readonly number[],
  rgbB: readonly number[],
  amountToMix: number,
) {
  const r = colorChannelMixer(rgbA[0]!, rgbB[0]!, amountToMix);
  const g = colorChannelMixer(rgbA[1]!, rgbB[1]!, amountToMix);
  const b = colorChannelMixer(rgbA[2]!, rgbB[2]!, amountToMix);

  return `rgb(${r},${g},${b})`;
}

/** From: https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb */
function hexToRGB(
  hex: string,
): { r: number; g: number; b: number } | undefined {
  const result = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex);
  if (result === null) {
    return undefined;
  }

  // We can't use `parseIntSafe` because we need to use a radix of 16.
  const r = Number.parseInt(result[1]!, 16);
  if (Number.isNaN(r)) {
    return undefined;
  }

  // We can't use `parseIntSafe` because we need to use a radix of 16.
  const g = Number.parseInt(result[2]!, 16);
  if (Number.isNaN(g)) {
    return undefined;
  }

  // We can't use `parseIntSafe` because we need to use a radix of 16.
  const b = Number.parseInt(result[3]!, 16);
  if (Number.isNaN(b)) {
    return undefined;
  }

  return {
    r,
    g,
    b,
  };
}
